import { Autocomplete } from "@mui/lab";
import {
	Button,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	Grid2,
	Snackbar,
	SnackbarCloseReason,
	TextField,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import * as React from "react";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import MesfModal from "../../../../../components/modals/modal.mesf";
import { Alert } from "../../../../../controls/alert";
import { HelmetDexteel } from "../../../../../helmet/HelmetDexteel";
import {
	Action,
	CacheConfiguration,
	CacheConfigurationFormValues,
	INITIAL_VALUES,
} from "../../models/CacheConfiguration";
import { upsertQueryCacheConfiguration } from "../../repositories/query-cache-repository";

type Props = {
	show: boolean;
	configuration: CacheConfiguration | null;
	actions: Action[];
	configuredActionIds: number[];
	onHide: (shouldUpdate: boolean) => void;
	suffixTitle?: string;
};

export const UpsertCachedAction = ({
	show,
	configuration,
	actions,
	configuredActionIds,
	onHide,
	suffixTitle,
}: Props) => {
	const [openSnackbar, setOpenSnackbar] = useState(false);
	const [message, setMessage] = useState("");
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState("");

	const isEdit = !!configuration;

	const {
		control,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<CacheConfigurationFormValues>({ defaultValues: INITIAL_VALUES });

	// Only read actions can be offered. Caching a write would hand a stored answer back to a call
	// meant to change something, and nothing else in the system would stop it, so the guard is
	// here: the name has to contain "get". It is the naming convention every read procedure in the
	// product follows, and it keeps Insert/Update/Delete/Upsert out of the list by construction.
	// One configuration per action too, so on a new one the already configured ones are not offered.
	const selectableActions = actions.filter(
		(a) =>
			(a.ActionName ?? "").toLowerCase().includes("get") &&
			(isEdit || !configuredActionIds.includes(a.ActionId)),
	);

	const saveConfiguration = useMutation({
		mutationFn: upsertQueryCacheConfiguration,
		onSuccess: () => {
			setMessage(
				`The cached action was ${isEdit ? "updated" : "created"} successfully`,
			);
			setOpenSnackbar(true);
			onHide(true);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => {
			setIsSubmitLoading(false);
		},
	});

	const onSubmit: SubmitHandler<CacheConfigurationFormValues> = async (
		data,
	) => {
		setIsSubmitLoading(true);
		await saveConfiguration.mutate({
			actionId: data.action!.ActionId,
			ttlSeconds: Number(data.ttlSeconds),
			isActive: data.isActive,
			comments: data.comments?.trim() ? data.comments.trim() : null,
		});
	};

	const handleClose = (
		event?: React.SyntheticEvent<any> | Event,
		reason?: SnackbarCloseReason,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setOpenSnackbar(false);
	};

	useEffect(() => {
		if (!show) return;

		setIsSubmitLoading(false);
		reset(
			configuration
				? {
						action:
							actions.find((a) => a.ActionId === configuration.ActionId) ||
							null,
						ttlSeconds: String(configuration.TTLSeconds ?? ""),
						isActive: !!configuration.IsActive,
						comments: configuration.Comments ?? "",
					}
				: INITIAL_VALUES,
		);
	}, [show, configuration, actions]);

	return (
		<>
			{show && (
				<HelmetDexteel
					title={`${isEdit ? "Edit Cached Action" : "New Cached Action"}${
						suffixTitle ? ` - ${suffixTitle}` : ""
					}`}
				/>
			)}
			<MesfModal
				title={isEdit ? "EDIT CACHED ACTION" : "NEW CACHED ACTION"}
				open={show}
				handleClose={() => onHide(false)}
				id="upsert-cached-action-modal"
				maxWidth="md"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content dividers>
						<Grid2 container spacing={2} alignItems="center">
							<Grid2 size={{ md: 12, xs: 12 }}>
								<Controller
									control={control}
									name="action"
									rules={{ required: true }}
									render={({ field }) => (
										<Autocomplete<Action>
											options={selectableActions}
											getOptionLabel={(option) => option.ActionName}
											isOptionEqualToValue={(option, value) =>
												option.ActionId === value.ActionId
											}
											value={field.value}
											onChange={(_, value) => field.onChange(value)}
											disabled={isEdit}
											size="small"
											renderInput={(params: any) => (
												<TextField
													{...params}
													label="Action"
													variant="outlined"
													error={!!errors.action}
													helperText={
														errors.action ? "Action is required" : undefined
													}
												/>
											)}
										/>
									)}
								/>
							</Grid2>

							<Grid2 size={{ md: 4, xs: 12 }}>
								<Controller
									control={control}
									name="ttlSeconds"
									rules={{
										required: "TTL is required",
										validate: (value) =>
											(Number.isInteger(Number(value)) && Number(value) > 0) ||
											"Must be a whole number greater than zero",
									}}
									render={({ field }) => (
										<TextField
											label="TTL (seconds)"
											value={field.value ?? ""}
											onChange={field.onChange}
											variant="outlined"
											size="small"
											type="number"
											fullWidth
											error={!!errors.ttlSeconds}
											helperText={errors.ttlSeconds?.message}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ md: 8, xs: 12 }}>
								<Controller
									control={control}
									name="isActive"
									render={({ field }) => (
										<FormControlLabel
											control={
												<Checkbox
													checked={!!field.value}
													onChange={field.onChange}
													color="primary"
												/>
											}
											label="Active"
										/>
									)}
								/>
							</Grid2>

							<Grid2 size={{ md: 12, xs: 12 }}>
								<Controller
									control={control}
									name="comments"
									render={({ field }) => (
										<TextField
											label="Comments"
											value={field.value ?? ""}
											onChange={field.onChange}
											variant="outlined"
											size="small"
											fullWidth
										/>
									)}
								/>
							</Grid2>
						</Grid2>
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: 16 }}>
						<Grid2 container spacing={2} justifyContent="flex-end">
							<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={() => onHide(false)}
								>
									Cancel
								</Button>
							</Grid2>
							<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									startIcon={
										isSubmitLoading && <CircularProgress size="1rem" />
									}
									disabled={isSubmitLoading}
									variant="contained"
									color="primary"
									type="submit"
								>
									Save
								</Button>
							</Grid2>
						</Grid2>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<Snackbar
				open={openSnackbar}
				autoHideDuration={2500}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="success" onClose={handleClose}>
					{message}
				</Alert>
			</Snackbar>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error Saving Cached Action"
			/>
		</>
	);
};
